import { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { useActivity } from '../hooks/useStatistics';
import { KartiCoachBubble } from '../components/brand/KartiCoach';
import { KARTI_LEVELS, activityContext, useKartiLevel } from '../lib/karti';
import { cn } from '../lib/utils';
import { useUpdateProfile, useChangePassword, useDeleteAccount } from '../hooks/useUsers';
import { useToast } from '../components/ui/Toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';

export default function ProfilePage() {
  const { user } = useAuthStore();
  const { showToast } = useToast();
  const updateProfileMutation = useUpdateProfile();
  const changePasswordMutation = useChangePassword();
  const deleteAccountMutation = useDeleteAccount();
  const { data: activity } = useActivity();
  const [kartiLevel, setKartiLevel] = useKartiLevel();

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [displayname, setDisplayname] = useState(user?.displayname || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayname.trim()) {
      showToast('Anzeigename darf nicht leer sein', 'error');
      return;
    }

    try {
      await updateProfileMutation.mutateAsync({ displayname });
      showToast('Profil aktualisiert', 'success');
      setShowEditDialog(false);
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Aktualisieren', 'error');
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      showToast('Passwort muss mindestens 8 Zeichen lang sein', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast('Passwörter stimmen nicht überein', 'error');
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        currentPassword,
        newPassword,
      });
      showToast('Passwort geändert', 'success');
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Ändern', 'error');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== user?.nickname) {
      showToast('Bestätigung ungültig', 'error');
      return;
    }

    try {
      await deleteAccountMutation.mutateAsync();
      showToast('Account gelöscht', 'success');
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Fehler beim Löschen', 'error');
    }
  };

  const openEditDialog = () => {
    setDisplayname(user?.displayname || '');
    setShowEditDialog(true);
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5 animate-fadein">
      <div className="space-y-2">
        <div className="label-mono">PROFIL</div>
        <h1 className="text-[34px] font-extrabold leading-none tracking-[-0.035em]">
          Dein <span className="highlight">Konto</span>
        </h1>
        <p className="text-body">Verwalte deine Kontoinformationen</p>
      </div>

      <KartiCoachBubble screen="profile" ctx={activityContext(activity)} />

      {/* Karti settings */}
      <Card>
        <CardHeader>
          <CardTitle>Wie hartnäckig darf Karti sein?</CardTitle>
          <CardDescription>Bestimmt, wie frech Karti dich ans Lernen erinnert</CardDescription>
        </CardHeader>
        <CardContent>
          <div role="radiogroup" aria-label="Wie hartnäckig darf Karti sein?" className="grid grid-cols-3 gap-2">
            {KARTI_LEVELS.map((l) => (
              <button
                key={l.id}
                type="button"
                role="radio"
                aria-checked={kartiLevel === l.id}
                onClick={() => setKartiLevel(l.id)}
                className={cn(
                  'press h-11 rounded-tile border-2 border-ink text-[15px] font-bold shadow-hard-sm',
                  kartiLevel === l.id ? 'bg-marker' : 'bg-cream'
                )}
              >
                {l.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle>Kontoinformationen</CardTitle>
          <CardDescription>Ihre persönlichen Daten</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label-mono">
                Nickname
              </label>
              <p className="text-[17px] font-bold">{user?.nickname}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Nickname kann nicht geändert werden
              </p>
            </div>

            <div>
              <label className="label-mono">
                Anzeigename
              </label>
              <p className="text-[17px] font-bold">{user?.displayname}</p>
            </div>
          </div>

          <div>
            <label className="label-mono">
              Mitglied seit
            </label>
            <p className="text-[17px] font-bold">
              {user?.createdAt
                ? new Date(user.createdAt).toLocaleDateString('de-DE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : '-'}
            </p>
          </div>

          <Button variant="accent" onClick={openEditDialog} className="w-full sm:w-auto">
            Profil bearbeiten
          </Button>
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle>Sicherheit</CardTitle>
          <CardDescription>Passwort und Sicherheitseinstellungen</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-3 rounded-tile border-2 border-ink bg-paper p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-bold">Passwort</h3>
              <p className="text-sm text-muted-foreground">
                Ändern Sie Ihr Passwort regelmäßig
              </p>
            </div>
            <Button variant="outline" onClick={() => setShowPasswordDialog(true)}>
              Ändern
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><span className="h-3 w-3 rounded-full border-2 border-ink bg-wrong" />Gefahrenzone</CardTitle>
          <CardDescription>
            Diese Aktionen können nicht rückgängig gemacht werden
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-3 rounded-tile border-2 border-ink bg-paper p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-bold">Account löschen</h3>
              <p className="text-sm text-muted-foreground">
                Löscht Ihren Account und alle zugehörigen Daten permanent
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
            >
              Löschen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <form onSubmit={handleUpdateProfile}>
            <DialogHeader>
              <DialogTitle>Profil bearbeiten</DialogTitle>
              <DialogDescription>
                Ändern Sie Ihre Profilinformationen
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="displayname" className="label-mono">
                  Anzeigename
                </label>
                <Input
                  id="displayname"
                  value={displayname}
                  onChange={(e) => setDisplayname(e.target.value)}
                  placeholder="Max Mustermann"
                  required
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
              >
                Abbrechen
              </Button>
              <Button type="submit" disabled={updateProfileMutation.isPending}>
                {updateProfileMutation.isPending ? 'Speichere...' : 'Speichern'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <form onSubmit={handleChangePassword}>
            <DialogHeader>
              <DialogTitle>Passwort ändern</DialogTitle>
              <DialogDescription>
                Geben Sie Ihr aktuelles und neues Passwort ein
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="currentPassword" className="label-mono">
                  Aktuelles Passwort
                </label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="newPassword" className="label-mono">
                  Neues Passwort
                </label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={8}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Mindestens 8 Zeichen
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="label-mono">
                  Passwort bestätigen
                </label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPasswordDialog(false)}
              >
                Abbrechen
              </Button>
              <Button type="submit" disabled={changePasswordMutation.isPending}>
                {changePasswordMutation.isPending ? 'Ändere...' : 'Passwort ändern'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Account löschen?</DialogTitle>
            <DialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Alle Ihre Daten werden
              permanent gelöscht.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm">
              Geben Sie Ihren Nickname <span className="font-bold">{user?.nickname}</span> zur
              Bestätigung ein:
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={user?.nickname}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteConfirmText('');
              }}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={
                deleteConfirmText !== user?.nickname || deleteAccountMutation.isPending
              }
            >
              {deleteAccountMutation.isPending ? 'Lösche...' : 'Account löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
